/**
 * 3A attribution dashboard service
 *
 * Fetches pre-computed dashboard JSON from impact_serving.dashboard via
 * GET /api/impact-dashboard/:siteId/:kind.
 *
 * Types are GENERATED from JSON Schema files in backend-merged/schemas/impact/
 * using json-schema-to-typescript v16.0.0, source sha b797bd5.
 * See admin/src/types/impact-schema.ts and bots-schema.ts.
 *
 * schema_version is an integer; only version 1 is known by this client.
 * Any unknown version or error response → the component shows an error state.
 */

import { authFetch } from '@/utils/auth-fetch';
import type { ImpactServingDashboardDataWhereKindImpact, MonthlyOption, Option, AttrRow, Attribution } from '@/types/impact-schema';
import type { ImpactServingDashboardDataWhereKindBots } from '@/types/bots-schema';

// ── Re-export generated root types under component-facing aliases ─────────────

export type { ImpactServingDashboardDataWhereKindImpact };
export type { ImpactServingDashboardDataWhereKindBots };

export type ImpactDashboardData = ImpactServingDashboardDataWhereKindImpact;
export type BotsDashboardData   = ImpactServingDashboardDataWhereKindBots;

// ── Component-facing type aliases (generated originals in impact-schema.ts) ───

/** A filter option (dimension|value pair), from the generated schema. */
export type AttributionOption = Option;

/** One monthly attribution row. */
export type AttributionRow = AttrRow;

/** The `attribution.total` shape — inlined in the generated Attribution type. */
export type AttributionTotal = ImpactDashboardData['channels']['llm']['attribution']['total'];

/** Monthly per-option bands (cohort/control/share_*). Nullable bands omitted for AI-only options. */
export type MonthlyByChannelBands = MonthlyOption;

/** The attribution block (columns, rows, total, by_option). */
export type { Attribution };

/** The LLM channel from the impact dashboard (has assistants breakdown). */
export type Channel = ImpactServingDashboardDataWhereKindImpact['channels']['llm'];
/** The SEO channel from the impact dashboard (no assistants breakdown). */
export type ChannelSeo = ImpactServingDashboardDataWhereKindImpact['channels']['seo'];

// ── Service ───────────────────────────────────────────────────────────────────

export const SUPPORTED_SCHEMA_VERSION = 1;

export type DashboardData = ImpactDashboardData | BotsDashboardData;

export class ImpactDashboardError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly retryable: boolean
  ) {
    super(message);
    this.name = 'ImpactDashboardError';
  }
}

class ImpactDashboardService {
  async get(siteId: string, kind: 'impact' | 'bots'): Promise<DashboardData> {
    const res = await authFetch(`/api/impact-dashboard/${siteId}/${kind}`);

    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new ImpactDashboardError(
        body.error ?? `Dashboard unavailable (${res.status})`,
        res.status,
        res.status === 503
      );
    }

    const data = (await res.json()) as DashboardData;

    if (data.schema_version !== SUPPORTED_SCHEMA_VERSION) {
      throw new ImpactDashboardError(
        `Dashboard schema version ${data.schema_version} not supported — please upgrade`,
        0,
        false
      );
    }

    return data;
  }
}

export const impactDashboardService = new ImpactDashboardService();
